import { gql } from '@apollo/client';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useCurrentPressroom } from '../../../contexts/CurrentPressroomContext';
import { useGetTrackingInitialDataQuery } from '../../../generated/hooks';
import { track } from '../../../lib/tracker';
import routes from '../../../routes';

gql`
  query GetTrackingInitialData(
    $pressroomId: ID!
    $skipPressroom: Boolean!
    $sourceId: ID!
    $skipSource: Boolean!
  ) {
    me {
      id
      user {
        id
        name
        username
        email
        type
        locale: locale2
      }
    }
    source(id: $sourceId) @skip(if: $skipSource) {
      id
      currentPlan
      customerId
      trial
      accountInfo {
        buyerPersona
        companySize
        tier
      }
      origin
    }
    pressroom(id: $pressroomId) @skip(if: $skipPressroom) {
      id
      live
      features {
        id
        ...BetaFeatures
        ...CopilotFeatures
        duplicateMaterial: managed(feature: duplicateMaterial) {
          id
          enabled
        }
        cloneStoryMaterial
      }
    }
  }
  fragment CopilotFeatures on PressroomFeatures {
    id
    coPilotAiWriter: managed(feature: coPilotAiWriter) {
      id
      enabled
    }
    coPilotContactCleaner: managed(feature: coPilotContactCleaner) {
      id
      enabled
    }
    coPilotPublishSidebar: managed(feature: coPilotPublishSidebar) {
      id
      enabled
    }
    coPilotRecommendedJournalists: managed(
      feature: coPilotRecommendedJournalists
    ) {
      id
      enabled
    }
    coPilotPrImpactMail: managed(feature: coPilotPrImpactMail) {
      id
      enabled
    }
  }
`;

const Tracking: React.FC = () => {
  const location = useLocation();
  const { i18n } = useTranslation();

  const initialPageRef = useRef(true);

  const { sourceId, pressroomId } = useCurrentPressroom();

  const { data } = useGetTrackingInitialDataQuery({
    variables: {
      pressroomId: pressroomId ?? '',
      skipPressroom: !pressroomId,
      sourceId: sourceId ?? '',
      skipSource: !sourceId,
    },
  });

  const trackNativePage = useCallback(
    () =>
      track({
        event: 'Native Page',
        traits: {
          applicationLanguage: i18n.language,
        },
      }),
    [i18n.language],
  );

  const trackPagePattern = useCallback(
    () =>
      Object.values(routes).find(route => {
        if (!('matchPath' in route)) {
          return false;
        }

        const match = route.matchPath(location.pathname);
        if (match) {
          /**
           * Remove questionmarks
           */
          let path = match.pathname.replace('?', '');

          /**
           * Get the params with typed input.
           */
          const typedParams = path.match(/:??([^:]*?)\(/g);
          if (typedParams) {
            typedParams.forEach(typedParam => {
              const paramName = typedParam.substring(1, typedParam.length - 1);
              const param: string = (match.params as any)[paramName] ?? '';

              path = path.replace(`:${paramName}`, param);
            });
          }

          /**
           * Remove parentheses and its content
           */
          path = path.replace(/\([^()]*\)/g, '');

          track({
            event: 'Page',
            traits: {
              path,
              applicationLanguage: i18n.language,
            },
          });
          return true;
        }

        return false;
      }),
    [i18n.language, location.pathname],
  );

  /**
   * This is intentionally written as one useEffect to ensure eliminate of race
   * condition between calling identify before identify excists.
   */
  useEffect(() => {
    window.analyticsSuperTraits = {
      companySize: data?.source?.accountInfo?.companySize ?? undefined,
      customerId: data?.source?.customerId ?? undefined,
      customerOrigin: data?.source?.origin ?? undefined,
      liveNewsroom: data?.pressroom?.live,
      locale: data?.me?.user.locale ?? undefined,
      persona: data?.source?.accountInfo?.buyerPersona ?? undefined,
      plan: data?.source?.currentPlan ?? undefined,
      pressroomId: data?.pressroom?.id,
      sourceId: data?.source?.id,
      tier: data?.source?.accountInfo?.tier ?? undefined,
      trial: data?.source?.trial,
      type: data?.me?.user?.type,
      userId: data?.me?.user?.id,
      userEmail: data?.me?.user?.email,
    };

    if (data?.pressroom?.features)
      for (const key in data.pressroom.features) {
        if (!['id', '__typename'].includes(key)) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          if (data.pressroom.features[key]?.enabled) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            window.analyticsSuperTraits[`betaFeature_${key}`] =
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              data.pressroom.features[key].enabled;
          } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            window.analyticsSuperTraits[`betaFeature_${key}`] = data.pressroom.features[key];
          }
        }
      }

    if (data?.me?.user) {
      track({
        event: 'Identify',
        traits: {
          id: data.me?.user?.id,
          email: data.me?.user?.email,
          name: data.me?.user?.name,
          username: data.me?.user?.username,
        },
      });

      /**
       * First page tracking needs do be done after identify. Otherwise, the tracked
       * page wont contain the user id.
       */
      if (initialPageRef.current) {
        initialPageRef.current = false;
        trackNativePage();
        trackPagePattern();
      }
    }
  }, [
    data?.pressroom?.id,
    data?.pressroom?.live,
    data?.pressroom?.features,
    data?.source?.id,
    data?.source?.trial,
    data?.me?.user,
    trackNativePage,
    data?.source?.accountInfo?.buyerPersona,
    data?.source?.accountInfo?.companySize,
    data?.source?.accountInfo?.tier,
    data?.source?.customerId,
    data?.source?.currentPlan,
    data?.source?.origin,
    trackPagePattern,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: new pathname triggers event
  useEffect(() => {
    trackNativePage();
  }, [location.pathname, trackNativePage]);

  useEffect(() => {
    if (initialPageRef.current === false) {
      trackPagePattern();
    }
  }, [trackPagePattern]);

  return null;
};

export default Tracking;
